<template>
    <div class="card">

        <div class="card-header">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.sessions') }}</h3>
                </div>
                <div class="col-6 text-right">
                    <button-radio-group 
                        :options="orderOptions"
                        button-classes="btn-secondary btn-sm"
                        v-model="order"
                        class="d-inline">
                    </button-radio-group>
                </div>
            </div>
        </div>

        <p class="card-body mt-0 mb-0" v-if="empty">
            <strong>{{ $t('pwa.no-session') }}</strong>
        </p>

        <div v-if="!empty">
            
            <div class="filters table-control col-12 d-flex justify-content-center justify-content-sm-between flex-wrap pr-0">

                <form @submit.prevent="submitSearch" style="width:180px;">
                    <el-input v-model="searchField"
                        :placeholder="$t('common.search')"
                        prefix-icon="el-icon-search"
                        clearable>
                    </el-input>
                </form>

                <div class="text-right" v-if="!$auth.offline">
                    <actions-dropdown :actions="filterActions" :large="true" class="float-right mt-2">
                    </actions-dropdown>

                    <base-select
                        :placeholder="$t('common.older-than')"
                        v-model="olderThan"
                        :options="olderThanOptions"
                        :sort="false"
                        clearable
                        style="padding-right:2.2rem;width:160px;"
                    >
                    </base-select>
                </div>

            </div>

            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="sortedSessions">

                <el-table-column class-name="first-cell">
                    <template v-slot="{row}">

                        <span :class="'float-left status-bullet text-'+row.sync.color+' ml-1'">
                            <i class="fas fa-circle"></i>
                        </span>
                        
                        <h3 class="mb-0 pl-3" v-if="!trash">
                            <a href="#" @click.prevent="openSession(row)">
                                {{ row.simulation.name }}
                            </a>
                        </h3>
                        <h3 class="mb-0 pl-3" v-else>
                            {{ row.simulation.name }}
                        </h3>

                        <span class="d-block pl-3">
                            {{ row.registration.degree.name }}
                        </span>

                    </template>
                </el-table-column>

                <el-table-column class-name="avatar-cell">
                    <template v-slot="{row}">
                        <div>
                            <div class="avatar-group">
                                <span class="avatar rounded-circle" v-if="row.learner1">
                                    <img :src="row.learner1.avatar">
                                </span>
                                <span class="avatar rounded-circle" v-if="row.learner2">
                                    <img :src="row.learner2.avatar">
                                </span>
                            </div>
                            <h5 class="mb-1" v-if="row.learner1">
                                {{ row.learner1.name }}
                            </h5>
                            <h5 class="mb-0" v-if="row.learner2">
                                {{ row.learner2.name }}
                            </h5>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">

                        <span class="d-block mb-0">
                            <i class="fas fa-users mr-1"></i> {{ row.registration.cohort.name }}
                        </span>

                        <span class="d-block mb-0">
                            <i class="fas fa-calendar-alt mr-1"></i> {{ date(row) }}
                        </span>

                    </template>
                </el-table-column>

                <el-table-column align="right" width="110" v-if="!trash">
                    <template v-slot="{row}">
                        <div class="p-1">

                            <base-button type="warning" size="sm" class="ml-1" @click="openSession(row)"
                                    v-if="!row.ended_at">
                                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
                            </base-button>

                            <base-button type="secondary" size="sm" class="ml-1" @click="openSession(row)"
                                    v-if="row.ended_at">
                                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
                            </base-button>

                        </div>
                    </template>
                </el-table-column>
                
                <el-table-column align="right" width="30" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)" :large="true" v-if="!$auth.offline">
                            </actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>
        </div>

        <portal to="modals">

            <confirm-modal v-model="modals.offlineConfirmRemove">
                <p>{{ $t('pwa.remove-confirm') }}</p>
            </confirm-modal>

            <sync-modal v-model="modals.offlineSync" :sync="sync" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import SyncModal from '../offline/SyncModal'
    import moment from 'moment'

    export default {

        components: {
            SyncModal
        },

        props: {
            trash: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                sessions: [],
                moment: moment,
                order: 'asc',
                modals: new ModalsController(['offlineError', 'offlineConfirmRemove', 'offlineSync']),
                offlineErrorMessage: null,
                sync: {
                    endpoint: '/astral/api/front/offline/sessions',
                    params: {},
                },
                searchField: '',
                searchFilter: '',
                olderThan: null,
                olderThanOptions: {
                    1: '1 ' + this.$t('common.day'),
                    7: '1 ' + this.$t('common.week'),
                    30: '1 ' + this.$t('common.month'),
                    90: '3 ' + this.$t('common.months'),
                    365: '1 ' + this.$t('common.year'),
                }
            }
        },

        created() {
            this.modals.init(this, 'modals')
            this.refresh()
        },

        computed: {

            empty() {
                return this.sessions.length == 0
            },

            orderOptions() {
                return [
                    { value: 'asc', label: '<i class="fas fa-arrow-down"></i>' },
                    { value: 'desc', label: '<i class="fas fa-arrow-up"></i>' },
                ]
            },

            searchValue() {
                return this.searchFilter.trim().toLowerCase()
            },

            sortedSessions() {

                // Search filter.
                let sessions = this.sessions.filter(session => !this.searchValue
                    || session.simulation.name.toLowerCase().includes(this.searchValue)
                    || session.registration.degree.name.toLowerCase().includes(this.searchValue)
                    || session.registration.cohort.name.toLowerCase().includes(this.searchValue)
                )

                // Older than filter.
                sessions = sessions.filter(session => {
                    let delay = (new Date() - new Date(session.sync.downloaded_at))
                    return !this.olderThan || delay > 1000 * 60 * 60 * 24 * this.olderThan
                })

                // Sorting.
                return sessions.sort((a, b) => {
                    if (this.order == 'asc' && a.sync.downloaded_at > b.sync.downloaded_at
                        || this.order == 'desc' && a.sync.downloaded_at <= b.sync.downloaded_at
                    ) {
                        return 1
                    } else {
                        return -1
                    }
                })
            },

            filterActions() {
                if (this.trash) {
                    return [{
                        label: this.$t('pwa.remove-from-trash'),
                        handler: this.hardRemoveSessions,
                    }]
                } else {
                    return [{
                        label: this.$t('pwa.remove'),
                        handler: this.removeSessions,
                    }]
                }
            },
        },

        watch: {
            searchField(val) {
                if (val === '') {
                    this.submitSearch()
                }
            }
        },

        methods: {

            submitSearch() {
                this.searchFilter = this.searchField
            },

            refresh() {
                axios.get('/astral/api/front/offline/sessions', { params: {
                    deleted: this.trash
                }}).then(resp => {
                    this.sessions = resp.data.data
                })
            },

            actions(item) {
                if (this.trash) {
                    return [{
                        label: this.$t('pwa.restore'),
                        handler: this.restoreSession,
                        data: item
                    },{
                        label: this.$t('pwa.remove-from-trash'),
                        handler: this.hardRemoveSession,
                        data: item
                    }]
                }
                let actions = []
                if (item.sync.color == 'warning') {
                    actions.push({
                        label: this.$t('pwa.refresh'),
                        handler: this.updateSession,
                        data: item
                    })
                }
                if (item.sync.color == 'danger') {
                    actions.push({
                        label: this.$t('pwa.sync'),
                        handler: this.syncSession,
                        data: item
                    })
                }
                actions.push({
                    label: this.$t('pwa.remove'),
                    handler: this.removeSession,
                    data: item
                })
                if (item.sync.color != 'danger') {
                    actions.push({
                        label: this.$t('pwa.resync'),
                        handler: this.resyncSession,
                        data: item
                    })
                }
                return actions
            },

            updateSession(item) {
                axios.post('/astral/api/front/offline/sessions', {
                    registration_id: item.registration.id,
                    simulation_id: item.simulation.id,
                    session_id: item.session.id,
                })
                .then(resp => {
                    this.sessions = resp.data.data
                }).catch(this.handleOfflineError)
            },

            syncSession(item, force = false) {
                this.sync.params.registration_id = item.registration.id
                this.sync.params.simulation_id = item.simulation.id
                this.sync.params.session_id = item.session.id
                this.sync.params.force = force
                this.modals.open('offlineSync')
            },

            resyncSession(item) {
                this.syncSession(item, true)
            },

            syncDone() {
                this.refresh()
            },

            removeSession(item) {
                this.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.removeSessionConfirmed(item)
                    }
                })
            },

            removeSessionConfirmed(item, callback) {
                axios.delete('/astral/api/front/offline/sessions', { data: {
                    registration_id: item.registration.id,
                    simulation_id: item.simulation.id,
                    session_id: item.session.id,
                }})
                .then(resp => {
                    this.sessions = resp.data.data
                    if (callback !== undefined) {
                        callback()
                    }
                }).catch(this.handleOfflineError)
            },

            hardRemoveSession(item) {
                this.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.hardRemoveSessionConfirmed(item)
                    }
                })
            },

            hardRemoveSessionConfirmed(item, callback) {
                axios.delete('/astral/api/front/offline/sessions/remove', { data: {
                    registration_id: item.registration.id,
                    simulation_id: item.simulation.id,
                    session_id: item.session.id,
                }})
                .then(resp => {
                    this.sessions = resp.data.data
                    if (callback !== undefined) {
                        callback()
                    }
                }).catch(this.handleOfflineError)
            },

            restoreSession(item) {
                axios.delete('/astral/api/front/offline/sessions/restore', { data: {
                    registration_id: item.registration.id,
                    simulation_id: item.simulation.id,
                    session_id: item.session.id,
                }})
                .then(resp => {
                    this.sessions = resp.data.data
                }).catch(this.handleOfflineError)
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },

            removeSessions() {
                this.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.removeSessionsConfirmed()
                    }
                })
            },

            removeSessionsConfirmed() {
                if (!this.sortedSessions.length == 0) {
                    this.removeSessionConfirmed(this.sortedSessions[0], this.removeSessionsConfirmed)
                }
            },

            hardRemoveSessions() {
                this.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.hardRemoveSessionsConfirmed()
                    }
                })
            },

            hardRemoveSessionsConfirmed() {
                if (!this.sortedSessions.length == 0) {
                    this.hardRemoveSessionConfirmed(this.sortedSessions[0], this.hardRemoveSessionsConfirmed)
                }
            },

            date(row) {
                return this.moment(row.sync.downloaded_at).format('YYYY/MM/DD')
            },
            
            openSession(row) {
                this.$router.push({ name: 'log-session',
                    params: { 
                        sessionId: row.session.id,
                        registrationId: row.registration.id,
                        simulationId: row.simulation.id,
                        groupId: 0,
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding-right: 0;
        font-size: 0.8rem;
    }
    .card >>> td.first-cell {
        padding-left: 1rem;
    }
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 0.5rem;
    }
    .text-warning {
        color: #fbb340 !important;
    }
    .status-bullet {
        font-size: 8px;
    }
    .card-header {
        border-bottom: 0 solid #fff;
    }

</style>
